import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Segment, Header, Grid, Table, Tab, Message, Form, Button } from 'semantic-ui-react';
import { oils } from '../services/api';

const fattyAcids = [
  { key: 'linoleic', label: 'Linoleic' },
  { key: 'linolenic', label: 'Linolenic' },
  { key: 'oleic', label: 'Oleic' },
  { key: 'palmitic', label: 'Palmitic' },
  { key: 'stearic', label: 'Stearic' },
];
const breakdowns = [
  { key: 'saturated', label: 'Saturated' },
  { key: 'mono_unsaturated', label: 'Mono-unsaturated' },
  { key: 'poly_unsaturated', label: 'Poly-unsaturated' },
];
const properties = [
  { key: 'bubbly', label: 'Bubbly' },
  { key: 'cleansing', label: 'Cleansing' },
  { key: 'condition', label: 'Condition' },
  { key: 'hardness', label: 'Hardness' },
  { key: 'longevity', label: 'Longevity' },
  { key: 'stability', label: 'Stable' },
];

function OilView() {
  const { id } = useParams();
  const [oil, setOil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    oils.get(id)
      .then(res => {
        setOil(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load oil details');
        setLoading(false);
      });
  }, [id]);

  if (loading) return <Segment loading style={{ minHeight: 200 }} />;
  if (error) return <Message negative>{error}</Message>;
  if (!oil) return null;

  // Calculate breakdowns and ratios if available
  const breakdown = oil.breakdown || {};
  const stats = oil.stats || {};
  const saponification = {
    KOH: oil.sap,
    NaOH: oil.sap ? (oil.sap * 0.713).toFixed(3) : '', // Example conversion
  };
  // Example: ratios (you may want to calculate these more precisely)
  const saturationRatio = `${breakdown.saturated || 0} : ${100 - (breakdown.saturated || 0)}`;

  const panes = [
    {
      menuItem: 'Posts',
      render: () => <Tab.Pane>No posts yet</Tab.Pane>,
    },
    {
      menuItem: 'Used In Recipes',
      render: () => <Tab.Pane>Used in recipes coming soon.</Tab.Pane>,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Soapee - {oil.name}</title>
      </Helmet>
      <Container>
        <Header as="h1" style={{ marginTop: '1em' }}>{oil.name}</Header>
        <Grid stackable columns={3}>
          <Grid.Column>
            <Segment>
              <Header as="h4">Fatty Acids</Header>
              <Table basic="very" compact>
                <Table.Body>
                  {fattyAcids.map(fa => (
                    <Table.Row key={fa.key}>
                      <Table.Cell>{fa.label}</Table.Cell>
                      <Table.Cell>{breakdown[fa.key] ?? 0}%</Table.Cell>
                    </Table.Row>
                  ))}
                  <Table.Row>
                    <Table.Cell>Breakdowns</Table.Cell>
                    <Table.Cell></Table.Cell>
                  </Table.Row>
                  {breakdowns.map(bd => (
                    <Table.Row key={bd.key}>
                      <Table.Cell>{bd.label}</Table.Cell>
                      <Table.Cell>{breakdown[bd.key] ?? 0}%</Table.Cell>
                    </Table.Row>
                  ))}
                  <Table.Row>
                    <Table.Cell>Ratios</Table.Cell>
                    <Table.Cell></Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Saturation Ratio</Table.Cell>
                    <Table.Cell>{saturationRatio}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment>
              <Header as="h4">Oil Properties</Header>
              <Table basic="very" compact>
                <Table.Body>
                  {properties.map(prop => (
                    <Table.Row key={prop.key}>
                      <Table.Cell>{prop.label}</Table.Cell>
                      <Table.Cell>{oil[prop.key] ?? 0}%</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment>
              <Header as="h4">Saponification Values</Header>
              <Table basic="very" compact>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>KOH</Table.Cell>
                    <Table.Cell>{saponification.KOH}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>NaOH</Table.Cell>
                    <Table.Cell>{saponification.NaOH}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Segment>
          </Grid.Column>
        </Grid>
        <Tab menu={{ secondary: true, pointing: true }} panes={panes} style={{ marginTop: 24 }} />
        <Segment style={{ marginTop: 24 }}>
          <Header as="h4">Create a New Post for {oil.name}</Header>
          <Form>
            <Form.Input label="Title" placeholder="Title" />
            <Form.TextArea label="Content" placeholder="Write your post..." />
            <Button primary>Create Post</Button>
          </Form>
        </Segment>
      </Container>
    </>
  );
}

export default OilView; 